<template>
  <div class="page-wrap">
    <el-card class="filter-card" shadow="never">
      <el-form :model="filterForm" inline>
        <el-form-item label="班级">
          <el-select v-model="filterForm.classId" placeholder="全部班级" clearable style="width:160px">
            <el-option v-for="c in classes" :key="c.id" :label="c.name" :value="c.id" />
          </el-select>
        </el-form-item>
        <el-form-item label="真实姓名">
          <el-input v-model="filterForm.realName" placeholder="真实姓名" clearable style="width:120px" />
        </el-form-item>
        <el-form-item label="试卷">
          <el-select
            v-model="filterForm.paperId"
            placeholder="全部试卷"
            clearable
            filterable
            remote
            reserve-keyword
            :remote-method="searchPapers"
            :loading="paperLoading"
            style="width:280px"
          >
            <el-option
              v-for="p in filteredPapers"
              :key="p.id"
              :label="p.name"
              :value="p.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="错误时间">
          <el-date-picker
            v-model="filterForm.timeRange"
            type="daterange"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            value-format="yyyy-MM-dd"
            style="width:400px"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="onFilterChange">搜索</el-button>
          <el-button @click="resetFilter">重置</el-button>
          <el-dropdown trigger="click" @command="v => exportPdf(v)">
            <el-button type="danger" icon="el-icon-download" :loading="exporting">
              导出错题试卷<i class="el-icon-arrow-down el-icon--right"></i>
            </el-button>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="student">学生版（无答案解析，留作答区）</el-dropdown-item>
              <el-dropdown-item command="teacher">教师版（含答案和解析）</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-form-item>
      </el-form>
      <div v-if="subjectName" class="subject-hint">任教学科：{{ subjectName }}</div>
    </el-card>

    <el-card shadow="never" v-if="!showDetail">
      <div class="table-toolbar">
        <span class="toolbar-title">错题试卷列表</span>
        <el-dropdown trigger="click" :disabled="selectedPaperKeys.length === 0" @command="v => exportMergedPapers(v)">
          <el-button
            type="primary"
            size="small"
            icon="el-icon-download"
            :disabled="selectedPaperKeys.length === 0"
            :loading="exporting"
          >
            合并导出 ({{ selectedPaperKeys.length }})<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="student">学生版（无答案解析，留作答区）</el-dropdown-item>
            <el-dropdown-item command="teacher">教师版（含答案和解析）</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <el-table :data="paperStats" border stripe v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="55" align="center" />
        <el-table-column type="index" :index="paperStatsIndex" width="60" label="#" />
        <el-table-column label="姓名" width="120">
          <template #default="{ row }">
            {{ row.realName || '-' }}
          </template>
        </el-table-column>
        <el-table-column label="试卷名称" min-width="200">
          <template #default="{ row }">
            <el-link type="primary" @click="viewPaperDetail(row)">{{ row.paperName }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label="学科" width="120">
          <template #default="{ row }">
            {{ getSubjectName(row.subjectId) }}
          </template>
        </el-table-column>
        <el-table-column label="题总数" prop="totalCount" width="90" align="center" />
        <el-table-column label="错题数量" prop="wrongCount" width="100" align="center" />
        <el-table-column label="错题率" prop="wrongRate" width="90" align="center" />
        <el-table-column label="最近错误时间" prop="lastWrongTime" width="170" />
        <el-table-column label="操作" width="150" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="mini" @click="viewPaperDetail(row)">查看错题</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="page + 1"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="size"
        :total="paperStatsTotal"
        layout="total, sizes, prev, pager, next, jumper"
      />
    </el-card>

    <el-card shadow="never" v-else>
      <div class="table-toolbar">
        <el-button size="small" @click="backToList">← 返回试卷列表</el-button>
        <span class="toolbar-title">{{ currentPaper.paperName }} - 错题详情</span>
        <el-dropdown trigger="click" @command="v => exportCurrentPaperAll(v)">
          <el-button type="danger" size="small" icon="el-icon-download" :loading="exporting">
            导出本卷全部错题<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="student">学生版（无答案解析，留作答区）</el-dropdown-item>
            <el-dropdown-item command="teacher">教师版（含答案和解析）</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
        <el-dropdown trigger="click" :disabled="!canExportSelected" @command="v => exportSelected(v)">
          <el-button size="small" :disabled="!canExportSelected" :loading="exporting">
            导出当前选中题目<i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="student">学生版（无答案解析，留作答区）</el-dropdown-item>
            <el-dropdown-item command="teacher">教师版（含答案和解析）</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
      <el-table
        :data="tableData"
        border
        stripe
        v-loading="loading"
        highlight-current-row
        :row-class-name="detailRowClassName"
        @row-click="handleDetailRowClick"
      >
        <el-table-column label="选择" width="55" align="center">
          <template slot-scope="scope">
            <el-radio v-model="selectedIndex" :label="scope.$index" @click.native.stop="selectDetailRow(scope.$index)">&nbsp;</el-radio>
          </template>
        </el-table-column>
        <el-table-column label="题号" type="index" width="60" />
        <el-table-column label="题目内容" min-width="500">
          <template #default="{ row }">
            <div class="question-detail">
              <div class="question-type-tag">
                <span :class="['type-tag', row.question && row.question.type]">{{ getQuestionType(row.question && row.question.type) }}</span>
              </div>
              <div class="question-content rich-html" v-html="renderHtml(row.question && row.question.content)"></div>
              <div v-if="row.question && (row.question.type === 'SINGLE' || row.question.type === 'MULTI')" class="options-list">
                <div v-for="opt in parseOptions(row.question && row.question.optionsJson)" :key="opt.key" class="option-item">
                  <span class="option-key">{{ opt.key }}</span>
                  <div class="option-value rich-html" v-html="renderHtml(opt.value)"></div>
                </div>
              </div>
              <div class="correct-answer">
                <span class="label">正确答案：</span>
                <span class="value">{{ row.question && row.question.answer }}</span>
              </div>
              <div v-if="row.question && row.question.analysis" class="analysis-box">
                <div class="analysis-title">解析</div>
                <div class="analysis-content rich-html" v-html="renderHtml(row.question && row.question.analysis)"></div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="错误次数" prop="wrongCount" width="100" align="center" />
        <el-table-column label="最近错误" prop="lastWrongTime" width="170" />
      </el-table>
      <el-pagination
        class="pagination"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :current-page="page + 1"
        :page-sizes="[10, 20, 50]"
        :page-size="size"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
      />
    </el-card>
  </div>
</template>

<script>
import filterLoader from '../../mixins/filterLoader'
import apiContext from '../../mixins/apiContext'
import { renderHtml } from '../../utils/renderHtml'

export default {
  name: 'TeacherWrongList',
  mixins: [filterLoader, apiContext],
  data() {
    return {
      filterForm: { classId: null, realName: null, paperId: null, userId: null, timeRange: [] },
      classes: [],
      subjectName: '',
      subjects: [],
      papers: [],
      filteredPapers: [],
      paperLoading: false,
      paperStats: [],
      paperStatsTotal: 0,
      tableData: [],
      selectedIndex: -1,
      selectedPaper: null,
      selectedPaperId: null,
      selectedPaperKey: null,
      selectedPaperKeys: [],
      showDetail: false,
      currentPaper: {},
      page: 0,
      size: 10,
      total: 0,
      loading: false,
      exporting: false
    }
  },
  created() {
    this.loadClasses()
    this.loadTeacherInfo()
    this.loadSubjectsFromFilters('subjects')
    this.loadPaperList('papers').then(() => {
      this.filteredPapers = this.papers
    })
    this.loadPaperStats()
  },
  computed: {
    selectedRow() {
      if (this.selectedIndex < 0) return null
      return this.tableData[this.selectedIndex] || null
    },
    canExportSelected() {
      return this.selectedIndex >= 0 && this.selectedRow != null
    }
  },
  watch: {
    selectedPaperKey(newVal) {
      if (newVal) {
        const [userId, paperId] = newVal.split('_')
        this.selectedPaper = this.paperStats.find(item =>
          String(item.userId) === userId && String(item.paperId) === paperId
        )
        this.selectedPaperId = paperId ? Number(paperId) : null
      } else {
        this.selectedPaper = null
        this.selectedPaperId = null
      }
    }
  },
  methods: {
    renderHtml,
    loadClasses() {
      this.api.get('/classes').then(data => {
        this.classes = data || []
      })
    },
    loadTeacherInfo() {
      this.api.get('/me').then(data => {
        this.subjectName = (data && data.subjectName) || ''
      })
    },
    buildParams() {
      const { classId, realName, paperId, timeRange, userId } = this.filterForm
      return {
        page: this.page,
        size: this.size,
        classId: classId || undefined,
        realName: realName || undefined,
        paperId: paperId || undefined,
        userId: userId || undefined,
        startTime: timeRange && timeRange[0] ? timeRange[0] : undefined,
        endTime: timeRange && timeRange[1] ? timeRange[1] : undefined
      }
    },
    buildExportParams(extra = {}) {
      const params = new URLSearchParams()
      const { classId, realName, paperId, timeRange, userId } = this.filterForm
      if (classId) params.append('classId', classId)
      if (realName) params.append('realName', realName)
      if (paperId) params.append('paperId', paperId)
      if (userId) params.append('userId', userId)
      if (timeRange && timeRange[0]) params.append('startTime', timeRange[0])
      if (timeRange && timeRange[1]) params.append('endTime', timeRange[1])
      Object.keys(extra).forEach(k => {
        if (extra[k] != null && extra[k] !== '') params.append(k, extra[k])
      })
      return params
    },
    downloadPdfResponse(res, filename) {
      return res.blob().then(blob => {
        if (blob.size === 0) {
          this.$message.error('导出的文件为空')
          return false
        }
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)
        return true
      })
    },
    fetchExportPdf(path, params, filename, onSuccess) {
      this.exporting = true
      const token = localStorage.getItem('admin_token')
      return fetch(`${this.apiPrefix}${path}?${params.toString()}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }).then(async res => {
        const contentType = res.headers.get('content-type') || ''
        if (contentType.includes('application/json')) {
          const err = await res.json()
          this.$message.error(err.message || '导出失败')
          return
        }
        if (!res.ok) {
          this.$message.error('导出失败，请稍后重试')
          return
        }
        const ok = await this.downloadPdfResponse(res, filename)
        if (ok && onSuccess) onSuccess()
      }).catch(err => {
        this.$message.error('导出失败: ' + (err.message || '网络错误'))
      }).finally(() => { this.exporting = false })
    },
    versionLabel(version) {
      return version === 'teacher' ? '教师版' : '学生版'
    },
    getSubjectName(subjectId) {
      if (!subjectId) return this.subjectName
      const subject = this.subjects.find(s => s.id === subjectId)
      return subject ? subject.name : this.subjectName
    },
    searchPapers(keyword) {
      if (!keyword || keyword.trim() === '') {
        this.filteredPapers = this.papers
        return
      }
      this.paperLoading = true
      const lowerKeyword = keyword.toLowerCase()
      this.filteredPapers = this.papers.filter(p =>
        p.name && p.name.toLowerCase().includes(lowerKeyword)
      )
      this.paperLoading = false
    },
    getQuestionType(type) {
      const typeMap = { SINGLE: '单选', MULTI: '多选', FILL: '填空', QA: '问答' }
      return typeMap[type] || type
    },
    parseOptions(optionsJson) {
      if (!optionsJson) return []
      try {
        const parsed = JSON.parse(optionsJson)
        if (Array.isArray(parsed) && parsed.length > 0) {
          if (typeof parsed[0] === 'object' && parsed[0].key) {
            return parsed
          }
          if (typeof parsed[0] === 'string') {
            return parsed.map(str => {
              const match = str.match(/^([A-D])\.(.+)$/)
              if (match) {
                return { key: match[1], value: match[2].trim() }
              }
              return { key: str.charAt(0), value: str }
            })
          }
        }
        return []
      } catch (e) {
        return []
      }
    },
    loadPaperStats() {
      this.loading = true
      this.api.get('/wrong/paperStats', { params: this.buildParams() })
        .then(data => {
          this.paperStats = (data && data.content) ? data.content : (data || [])
          this.paperStatsTotal = (data && data.totalElements != null) ? data.totalElements : this.paperStats.length
          this.selectedPaper = null
          this.selectedPaperId = null
          this.selectedPaperKeys = []
        }).finally(() => { this.loading = false })
    },
    paperStatsIndex(index) {
      return this.page * this.size + index + 1
    },
    viewPaperDetail(paper) {
      this.currentPaper = paper
      this.showDetail = true
      this.page = 0
      this.filterForm.paperId = paper.paperId
      this.filterForm.userId = paper.userId
      this.loadWrongQuestions()
    },
    backToList() {
      this.showDetail = false
      this.currentPaper = {}
      this.filterForm.paperId = null
      this.filterForm.userId = null
      this.selectedIndex = -1
      this.selectedPaperKey = null
      this.loadPaperStats()
    },
    loadWrongQuestions() {
      this.loading = true
      this.api.get('/wrong/list', { params: this.buildParams() })
        .then(data => {
          this.tableData = this.normalizeWrongRows(data.content || [])
          this.total = data.totalElements || 0
          this.selectedIndex = -1
        }).finally(() => { this.loading = false })
    },
    loadList() {
      if (this.showDetail) {
        this.loadWrongQuestions()
      } else {
        this.loadPaperStats()
      }
    },
    onFilterChange() {
      this.page = 0
      if (this.showDetail) {
        this.loadWrongQuestions()
      } else {
        this.loadPaperStats()
      }
    },
    resetFilter() {
      this.filterForm = { classId: null, realName: null, paperId: null, userId: null, timeRange: [] }
      this.selectedIndex = -1
      this.selectedPaper = null
      this.selectedPaperId = null
      this.selectedPaperKey = null
      this.selectedPaperKeys = []
      if (this.showDetail) {
        this.backToList()
      } else {
        this.onFilterChange()
      }
    },
    handleSelectionChange(selection) {
      this.selectedPaperKeys = selection
    },
    handlePageChange(val) { this.page = val - 1; this.loadList() },
    handleSizeChange(val) { this.size = val; this.page = 0; this.loadList() },
    normalizeWrongRows(rows) {
      return (rows || []).map(row => ({
        ...row,
        id: row.id != null ? row.id : row.wrongQuestionId
      }))
    },
    resolveSelectedExportParams(version) {
      const row = this.selectedRow
      const label = this.versionLabel(version)
      if (row && row.id != null && row.id !== '') {
        return {
          path: '/wrong/exportPdfByIds',
          params: this.buildExportParams({ ids: String(row.id), version }),
          filename: `选中错题_${label}.pdf`
        }
      }
      const questionId = row && row.question && row.question.id
      const userId = this.filterForm.userId || (this.currentPaper && this.currentPaper.userId)
      const paperId = (this.currentPaper && this.currentPaper.paperId) || this.filterForm.paperId
      if (questionId && userId) {
        return {
          path: '/wrong/exportPdfBySelection',
          params: this.buildExportParams({
            userId,
            questionId,
            paperId: paperId || undefined,
            version
          }),
          filename: `选中错题_${label}.pdf`
        }
      }
      return null
    },
    handleDetailRowClick(row) {
      const index = this.tableData.indexOf(row)
      if (index >= 0) this.selectedIndex = index
    },
    selectDetailRow(index) {
      this.selectedIndex = index
    },
    detailRowClassName({ rowIndex }) {
      return this.selectedIndex === rowIndex ? 'detail-row-selected' : ''
    },
    exportSelected(version) {
      if (!this.canExportSelected) {
        this.$message.warning('请先点击题目行选中要导出的题目')
        return
      }
      const request = this.resolveSelectedExportParams(version)
      if (!request) {
        this.$message.error('无法识别选中题目，请刷新后重试')
        return
      }
      this.fetchExportPdf(request.path, request.params, request.filename)
    },
    exportCurrentPaperAll(version) {
      const paper = this.currentPaper || {}
      if (!paper.paperId) {
        this.$message.warning('缺少试卷信息')
        return
      }
      const label = this.versionLabel(version)
      const params = this.buildExportParams({
        paperId: paper.paperId,
        userId: paper.userId,
        realName: paper.realName || undefined,
        version
      })
      this.fetchExportPdf('/wrong/exportPdf', params, `${paper.paperName || '错题试卷'}_全部错题_${label}.pdf`)
    },
    exportPdf(version) {
      if (this.showDetail) {
        this.exportCurrentPaperAll(version)
        return
      }

      const label = this.versionLabel(version)
      if (this.selectedPaperKeys.length > 0) {
        const paper = this.selectedPaperKeys[0]
        const params = this.buildExportParams({
          paperId: paper.paperId,
          userId: paper.userId,
          realName: paper.realName || undefined,
          version
        })
        this.fetchExportPdf('/wrong/exportPdf', params, `${paper.paperName}_错题试卷_${label}.pdf`)
        return
      }

      const params = this.buildExportParams({ version })
      this.fetchExportPdf('/wrong/exportPdf', params, `错题试卷_${label}_${new Date().toLocaleDateString()}.pdf`)
    },
    exportMergedPapers(version) {
      if (this.selectedPaperKeys.length === 0) {
        this.$message.warning('请先选择要导出的试卷')
        return
      }
      const label = this.versionLabel(version)
      const userPaperPairs = this.selectedPaperKeys.map(item => `${item.userId}:${item.paperId}`).join(',')
      const params = this.buildExportParams({ userPaperPairs, version })
      const dateStr = new Date().toLocaleDateString().replace(/\//g, '-')
      this.fetchExportPdf('/wrong/exportMergedPapers', params, `合并试卷-错题${dateStr}_${label}.pdf`,
        () => this.$message.success('合并导出成功'))
    }
  }
}
</script>

<style scoped>
.filter-card { margin-bottom: 16px; }
.subject-hint { font-size: 13px; color: #666; margin-top: 4px; }
.pagination { margin-top: 16px; text-align: right; }
.table-toolbar {
  margin-bottom: 16px;
  display: flex;
  gap: 10px;
  align-items: center;
}
:deep(.el-table__body tr) {
  cursor: pointer;
}
:deep(.detail-row-selected > td) {
  background-color: #ecf5ff !important;
}
.toolbar-title {
  font-size: 16px;
  font-weight: bold;
  flex: 1;
}
.question-detail { padding: 10px 0; }
.question-type-tag { margin-bottom: 10px; }
.type-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
}
.type-tag.SINGLE { background: #e6f7ff; color: #1890ff; }
.type-tag.MULTI { background: #fff7e6; color: #fa8c16; }
.type-tag.FILL { background: #f6ffed; color: #52c41a; }
.type-tag.QA { background: #fff1f0; color: #f5222d; }
.question-content {
  font-size: 15px;
  line-height: 1.8;
  color: #333;
  margin-bottom: 15px;
}
.options-list {
  margin: 15px 0;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
}
.option-item {
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
}
.option-item:last-child { border-bottom: none; }
.option-key {
  width: 28px;
  height: 28px;
  background: #f0f0f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-size: 14px;
  font-weight: 500;
  flex-shrink: 0;
}
.option-value {
  flex: 1;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
}
.correct-answer {
  margin: 15px 0;
  padding: 12px 15px;
  background: #f0f5ff;
  border-radius: 8px;
  border-left: 4px solid #1677ff;
}
.correct-answer .label { font-size: 14px; color: #666; }
.correct-answer .value {
  font-size: 16px;
  color: #1677ff;
  font-weight: 600;
  margin-left: 8px;
}
.analysis-box {
  margin-top: 15px;
  padding: 15px;
  background: #f6ffed;
  border-radius: 8px;
  border-left: 4px solid #52c41a;
}
.analysis-title {
  font-size: 14px;
  color: #52c41a;
  font-weight: 600;
  margin-bottom: 10px;
}
.analysis-content {
  font-size: 14px;
  line-height: 1.8;
  color: #333;
}
.question-content :deep(img),
.option-value :deep(img),
.analysis-content :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
  margin: 6px 0;
}
</style>
